import { useMemo } from "react";
import trackTemplate, {
  TEMPLATE_TRACKING_TYPES,
  type TemplateTrackExtras,
} from "../../../Utils/SupabaseServices/TrackTemplate";
import track, { TRACKING_TYPES } from "../../../Utils/SupabaseServices/Track";
import { TEMPLATE_ANALYTICS_EVENTS } from "../../../Utils/SupabaseServices/templateAnalyticsEvents";
import { updateRefClick } from "../../../Utils/SupabaseServices/ContentDatabase";
import { supabase } from "../../../config/supabaseClient";
import type { PostClickTrackingValue } from "../../../components/FeedPost/PostInfo/PostClickTrackingContext";
import { carouselTrackIdentity } from "../../../Utils/analytics/carouselAnalyticsKey";
import {
  legacyLibAnalyticsPathname,
  shouldMirrorLegacyLibAnalytics,
} from "../../../Utils/analytics/legacyLibAnalyticsPath";
import { resolveTemplatePageKeyFromPathname } from "./websitePublicTemplatePath";
import {
  readTrafficSource,
  useTemplateAnalyticsOptional,
} from "./TemplateAnalyticsContext";
import type { TemplateContentView, TemplateShopView } from "./types";

export type TemplateFeedPostTrackingMeta = {
  sectionId?: string;
  sectionType?: string;
  carouselSlug?: string;
  /** Full stayuptodate row — required so duplicate titles don't share analytics. */
  component?: Record<string, unknown> | null;
  contentView?: TemplateContentView;
  contentTab?: string;
  shopView?: TemplateShopView;
  contentCategory?: string;
  categoryPrimary?: string;
  categorySecondary?: string;
  categoryBrand?: string;
};

/**
 * Builds template-aware post-click handlers for feeds rendered on the public
 * website template. Used by:
 * - Search (`FeedOptimised` via `PostClickTrackingProvider`)
 * - Link in Bio (`FeedControls` + detail PostInfo shop/tagged)
 * - Content / Shop tab panels (`RenderComponentLoader` card clicks + PostInfo)
 *
 * Returns `undefined` when template analytics is disabled (editor preview).
 */
export function useTemplateFeedPostTracking(
  pageKey: string,
  meta?: TemplateFeedPostTrackingMeta,
): PostClickTrackingValue | undefined {
  const analytics = useTemplateAnalyticsOptional();
  const enabled = Boolean(analytics?.enabled && analytics?.userId);
  const userId = analytics?.userId ?? "";
  const username = analytics?.username ?? "";
  const routeName = analytics?.routeName ?? "";
  const sectionId = meta?.sectionId ?? pageKey;
  const sectionType = meta?.sectionType ?? "Feed";
  const carouselSlug = meta?.carouselSlug;
  const component = meta?.component;
  const contentView = meta?.contentView;
  const contentTab = meta?.contentTab;
  const shopView = meta?.shopView;
  const contentCategory = meta?.contentCategory;
  const categoryPrimary = meta?.categoryPrimary;
  const categorySecondary = meta?.categorySecondary;
  const categoryBrand = meta?.categoryBrand;

  return useMemo(() => {
    if (!enabled) return undefined;

    const componentIdentity = carouselTrackIdentity(component);
    const resolvedCarouselSlug =
      carouselSlug ||
      componentIdentity.component_id ||
      componentIdentity.unique_id ||
      undefined;

    const baseExtras = (
      overrides?: Partial<TemplateTrackExtras>,
    ): TemplateTrackExtras => ({
      user_id: userId,
      username,
      route_name: routeName,
      pathname: typeof window !== "undefined" ? window.location.pathname : "",
      source: readTrafficSource(),
      page_key: pageKey,
      title: "Website Template",
      ...overrides,
    });

    const buildDetails = (
      postId: string,
      mediaUrl: string,
      platform: any,
      name: any,
      extra?: Record<string, unknown>,
    ) => ({
      post_id: postId,
      media_url: mediaUrl,
      platform,
      name,
      ...componentIdentity,
      content_view: contentView,
      content_tab: contentTab,
      shop_view: shopView,
      content_category: contentCategory,
      category_primary: categoryPrimary,
      category_secondary: categorySecondary,
      category_brand: categoryBrand,
      ...extra,
    });

    /** Keep Website Analytics → Link in Bio (classic RPCs) in sync for LiB only. */
    const mirrorLegacyLib = (
      eventName: string,
      details: Record<string, unknown>,
    ) => {
      if (!username) return;
      const path =
        typeof window !== "undefined" ? window.location.pathname : "";
      const pathPageKey = resolveTemplatePageKeyFromPathname(path);
      // In-page LiB search attributes to page_key "search" — still mirror.
      if (
        !shouldMirrorLegacyLibAnalytics(path, pageKey) &&
        !shouldMirrorLegacyLibAnalytics(path, pathPageKey)
      ) {
        return;
      }
      void track(eventName, TRACKING_TYPES.EVENT, {
        pathname: legacyLibAnalyticsPathname(username),
        details,
        user_id: userId,
        title: "Stay Up To Date",
      });
    };

    const trackPostClick = (
      postId: string,
      mediaUrl: string,
      platform: any,
      name: any,
    ) => {
      const details = buildDetails(postId, mediaUrl, platform, name);
      void trackTemplate(
        TEMPLATE_ANALYTICS_EVENTS.POST_CLICK,
        TEMPLATE_TRACKING_TYPES.EVENT,
        baseExtras({
          section_id: sectionId,
          section_type: sectionType,
          entity_type: "post",
          entity_id: postId,
          entity_label: name || postId,
          carousel_slug: resolvedCarouselSlug,
          details,
        }),
      );
      mirrorLegacyLib("Post Click", details);
    };

    const handleRedirect: PostClickTrackingValue["handleRedirect"] = (
      postId,
      mediaUrl,
      platform,
      name,
      uid,
    ) => {
      if (!postId) return;
      trackPostClick(postId, mediaUrl, platform, name);
      const details = buildDetails(postId, mediaUrl, platform, name);
      void trackTemplate(
        TEMPLATE_ANALYTICS_EVENTS.ORIGINAL_POST,
        TEMPLATE_TRACKING_TYPES.EVENT,
        baseExtras({
          section_id: sectionId,
          section_type: sectionType,
          entity_type: "post",
          entity_id: postId,
          entity_label: name || postId,
          carousel_slug: resolvedCarouselSlug,
          details,
        }),
      );
      mirrorLegacyLib("Redirect to original post", details);
      void updateRefClick(postId, uid || userId, supabase);
    };

    const handleShop: PostClickTrackingValue["handleShop"] = (
      postId,
      mediaUrl,
      platform,
      name,
    ) => {
      if (!postId) return;
      trackPostClick(postId, mediaUrl, platform, name);
      const details = buildDetails(postId, mediaUrl, platform, name);
      void trackTemplate(
        TEMPLATE_ANALYTICS_EVENTS.SHOP_CLICK,
        TEMPLATE_TRACKING_TYPES.EVENT,
        baseExtras({
          section_id: sectionId,
          section_type: sectionType,
          entity_type: "product",
          entity_id: postId,
          entity_label: name || postId,
          carousel_slug: resolvedCarouselSlug,
          details,
        }),
      );
      mirrorLegacyLib("Redirect to shop link", details);
    };

    const handleTagged: PostClickTrackingValue["handleTagged"] = (
      postId,
      mediaUrl,
      platform,
      name,
    ) => {
      if (!postId) return;
      trackPostClick(postId, mediaUrl, platform, name);
      const details = buildDetails(postId, mediaUrl, platform, name, {
        link_kind: "tagged",
      });
      void trackTemplate(
        TEMPLATE_ANALYTICS_EVENTS.ORIGINAL_POST,
        TEMPLATE_TRACKING_TYPES.EVENT,
        baseExtras({
          section_id: sectionId,
          section_type: sectionType,
          entity_type: "post",
          entity_id: postId,
          entity_label: name || postId,
          carousel_slug: resolvedCarouselSlug,
          details,
        }),
      );
      mirrorLegacyLib("Redirect to original post", details);
    };

    return { handleRedirect, handleShop, handleTagged };
  }, [
    enabled,
    userId,
    username,
    routeName,
    pageKey,
    sectionId,
    sectionType,
    carouselSlug,
    component,
    contentView,
    contentTab,
    shopView,
    contentCategory,
    categoryPrimary,
    categorySecondary,
    categoryBrand,
  ]);
}
